import React, { forwardRef, ReactNode } from 'react';
import { FloatingFocusManager, FloatingNode, FloatingPortal } from '@floating-ui/react-dom-interactions';
import { css, cx } from '@linaria/atomic';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { CalendarDialogCalendar } from './calendar-dialog-calendar.component';
import type { UseDataPickerDialogResponse } from './use-date-picker';

const componentMotionDialogStyle1 = css`
  border: none;
  overflow: auto;
  background: white;
  padding-top: 0;
  box-shadow: ${theme.shadows.heavy};
  z-index: ${theme.zIndex.popover};
  &:focus {
    outline: none;
  }
`;

const datePickerDialogBottomStyle = css`
  border-radius: ${theme.spacing(0, 0, 1, 1)};
`;

const datePickerDialogTopStyle = css`
  border-radius: ${theme.spacing(1, 1, 0, 0)};
`;

type Props = UseDataPickerDialogResponse['dialogProps'] & {
  children: ReactNode;
  onFocus?: (event: React.FocusEvent<HTMLDialogElement>) => void;
};

type DragEventKeys =
  | 'onDrag'
  | 'onDragEnd'
  | 'onDragEnter'
  | 'onDragExit'
  | 'onDragLeave'
  | 'onDragOver'
  | 'onDragStart'
  | 'onAnimationStart';
type DialogProps = Omit<React.ComponentPropsWithoutRef<'dialog'>, DragEventKeys> & Props;

export const DatePickerDialogPre = forwardRef<HTMLDialogElement, DialogProps>(
  ({ open, nodeId, context, children, labelId, descriptionId, initialFocus, style: floatingStyle, ...props }, ref) => {
    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal>
          <AnimatePresence initial={false}>
            {open && (
              <FloatingFocusManager context={context} returnFocus={false} initialFocus={initialFocus}>
                <motion.dialog
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  ref={ref}
                  open={open}
                  aria-describedby={descriptionId}
                  aria-labelledby={labelId}
                  {...props}
                  style={floatingStyle}
                  className={cx(
                    componentMotionDialogStyle1,
                    context.placement.includes('bottom') ? datePickerDialogBottomStyle : datePickerDialogTopStyle,
                    props.className
                  )}
                >
                  {children}
                </motion.dialog>
              </FloatingFocusManager>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

const DatePickerDialogNamespace = Object.assign(DatePickerDialogPre, {
  Calendar: CalendarDialogCalendar,
});

export { DatePickerDialogNamespace as DatePickerDialog };
